<script lang="ts" setup>
import countTo from 'vue-count-to'
import { ref, watch } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  AutoRenewalNotice,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import PaymentMethodSelector from '@/composables/payment/components/PaymentMethodSelector.flipnovel.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '@/composables/payment/utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'
import { theme as brandTheme, colors } from '@/config/brands'

const props = defineProps<{
  config: SubscribePageConfig
}>()

// 使用订阅 composable（支持多支付方式）
const {
  subscribeProducts,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(subscribeProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAddToCart } = useFbqAddToCart()
watch(subscribeProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Membership', p.preferentialPrice ?? p.price ?? 0)
})

const active = ref(0)

// 定义样式变量供 CSS v-bind 使用
const redGradient = props.config.colors.redGradient
const whiteColor = '#FFFFFF'
const vipBgUrl = `url(${props.config.resources.vipBg})`
const primaryColor = colors?.primary
const subscribeCardBg = props.config.colors.subscribeCardBg || 'linear-gradient(90deg, #443431, #261b1a)'
const badgeGradient = props.config.colors.badgeGradient || 'linear-gradient(132deg, #F961BC 0%, #F74984 100%)'

const subscribeText1 = props.config.colors.subscribeText1
const subscribeText2 = props.config.colors.subscribeText2
const subscribeText3 = props.config.colors.subscribeText3
const globalBgColor = brandTheme.bgGlobal || '#121214'

function getItemBackground(item: MemberTopupSubscribeProduct) {
  return subscribeCardBg
}

const cycleTextGradient = props.config.colors.cycleTextGradient || 'linear-gradient(180deg, #98462D 0%, #2B2426 100%)'
</script>

<template>
  <div class="subscribe-cover bg-n-bg-global" :style="{ backgroundColor: globalBgColor }">
    <div class="px-20px">
      <div class="text-28px font-bold subscribe-text-1 mt-2">
        {{ config.texts.vipMembership }}
      </div>
      <div class="subscribe-text-3 text-14px">
        {{ config.texts.membershipDesc }}
      </div>
      <div class="subscribe-text-1 mt-2px text-14px">
        {{ config.texts.notJoinedYet }}
      </div>
    </div>

    <div class="member-wrap mt-12px mx-10px bg-n-bg-card b-rd-2 border border-n-border-primary px-10px pt-16px">
      <div v-for="(item, index) in subscribeProducts" :key="index">
        <div
          v-if="item.isShow"
          class="px-12px pt-18px pb-18px border-rd-12px subscribe-text-2 relative mb-10px transition-all duration-200"
          :style="{
            border: active === index ? `1px solid ${primaryColor}` : `1px solid ${colors?.border.primary}`,
            background: getItemBackground(item),
            backgroundSize: '100% 100%',
            color: subscribeText2,
          }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- 角标, 两种会员都有 -->
          <div v-if="item.cornerTip === 1 && item.marketingCopy" class="popular-tag">
            {{ item.marketingCopy }}
          </div>

          <!-- 折扣会员 -->
          <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-center">
            <div class="absolute top-0 right-0 font-bold text-32px leading-[30px] cycleBg">
              {{ item.cycleStr }}
            </div>

            <div class="flex-1 rr-discount-text">
              <div class="text-18px font-medium">
                {{ item.discountCoins }} Coins
              </div>
              <ul
                class="text-10px subscribe-text-1 rr-discount-list"
              >
                <li class="rr-discount-list-item">
                  <span class="rr-discount-dot" />
                  <span>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</span>
                </li>
                <li class="rr-discount-list-item">
                  <span class="rr-discount-dot" />
                  <span class="rr-earn-line">
                    {{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}
                  </span>
                </li>
              </ul>
            </div>
            <div class="flex-shrink-0 text-right relative rr-discount-price">
              <div class="text-18px font-bold">
                <div class="min-w-60px">
                  $ <countTo
                    :start-val="Number(item.discountOriginalPrice)"
                    :end-val="Number(item.preferentialPrice || item.price)"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
              </div>
              <div class="text-12px line-through subscribe-text-3">
                $ {{ item.discountOriginalPrice }}
              </div>
            </div>
          </div>

          <!-- 畅读会员 -->
          <div v-else class="flex items-center relative h-full">
            <div class="flex-1 flex flex-col justify-start">
              <div class="flex items-center gap-6px">
                <BasicFitText class="text-18px font-medium">
                  {{ item.cycleStr }}
                </BasicFitText>
                <div
                  v-if="shouldShowDailyPrice(item, isZero)"
                  class="flex-shrink-0 bg-[#F74984]/15 text-[#F74984] text-10px font-medium rounded-4px px-6px py-3px lh-8px"
                >
                  SAVE {{ getSavePercent(item) }}%
                </div>
              </div>
              <!-- 覆写文案(ZKSN-251):一行放得下则单行展示,放不下按 \n 拆行并逐行缩放 -->
              <div class="subscribe-text-1 text-12px leading-[16px]">
                <BasicFitLines v-if="item.subscribeTextOverridden" :text="item.subscribeText" />
                <template v-else>
                  {{ item.subscribeText }}
                </template>
              </div>
            </div>

            <div class="flex-shrink-0 text-right">
              <!-- 免费 -->
              <div v-if="isZero(item.preferentialPrice)">
                <div class="text-18px font-bold">
                  {{ config.texts.free }}
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </div>
              <!-- 无优惠价格 -->
              <div v-else-if="!item.preferentialPrice">
                <div class="text-18px font-bold whitespace-nowrap">
                  <template v-if="getUnitPriceValue(item)">
                    $ {{ getUnitPriceValue(item) }}<span class="text-12px font-normal subscribe-text-2"> / Day</span>
                  </template>
                  <template v-else>
                    $ {{ item.price }}
                  </template>
                </div>
                <div v-if="getUnitPriceValue(item)" class="subscribe-text-3 text-12px">
                  $ {{ item.price }}
                </div>
              </div>
              <!-- 有优惠价格 -->
              <div v-else>
                <div class="text-18px font-bold whitespace-nowrap">
                  <template v-if="shouldShowDailyPrice(item, isZero)">
                    $ {{ getDailyPrice(item) }}<span class="text-12px font-normal subscribe-text-2"> / Day</span>
                  </template>
                  <template v-else>
                    <span class="min-w-60px">
                      $ <countTo
                        :start-val="Number(item.price)"
                        :end-val="Number(item.preferentialPrice)"
                        :duration="3000"
                        :decimals="2"
                      />
                    </span>
                  </template>
                </div>
                <div class="text-12px">
                  <span class="subscribe-text-3 line-through">$ {{ item.price }}</span>
                  <span v-if="shouldShowPreferentialTotal(item, isZero)" class="subscribe-text-2 font-medium ml-4px">$ {{ item.preferentialPrice }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4px text-10px text-n-text-3 pb-100px">
        <p class="text-12px mt-6px">
          {{ config.texts.tips }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip1 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip2 }}
        </p>
        <!-- 自动续费提示(仅自然用户展示;订阅中心无充值商品,不含括号内容) -->
        <AutoRenewalNotice class="mt-6px" has-subscribe :has-topup="false" prefix="3. " />
      </div>

      <!-- 支付方式选择器固定在底部 -->
      <div v-if="showPaymentMethodSelector" class="fixed bottom-16px left-0 right-0 flex justify-center">
        <PaymentMethodSelector
          variant="large"
          :dark="config.darkPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.member-wrap {
  min-height: calc(100vh - 192px);
}

.subscribe-cover {
  width: 100%;
  min-height: 100vh;
  background: v-bind(vipBgUrl) no-repeat top left;
  background-size: 100% auto;
}

.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  padding: 0 8px;
  background: v-bind(badgeGradient);
  border-radius: 100px 100px 100px 0px;
  font-weight: 400;
  font-size: 10px;
  color: v-bind(whiteColor);
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.cycleBg {
  background: v-bind(cycleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.rr-discount-text,
.rr-discount-price {
  margin-top: 8px;
}

.rr-discount-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.rr-discount-list-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rr-discount-dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: currentColor;
  flex: 0 0 4px;
}

.subscribe-text-1 {
  color: v-bind(subscribeText1);
}
.subscribe-text-2 {
  color: v-bind(subscribeText2);
}
.subscribe-text-3 {
  color: v-bind(subscribeText3);
}

.rr-earn-line {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
